import { useQuery } from '@tanstack/react-query';
import { getWinProb } from '../api';

const W = 320;
const H = 110;
const PAD_L = 6;
const PAD_R = 6;
const PAD_T = 6;
const PAD_B = 16;

const COLORS = { home: '#22c55e', draw: '#737373', away: '#06b6d4' };

export default function WinProbChart({ matchId, homeName, awayName, preMatch, status }: {
  matchId: string;
  homeName: string;
  awayName: string;
  preMatch: { home: number; draw: number; away: number } | null;
  status: 'pre' | 'in' | 'post';
}) {
  const enableSnapshots = status !== 'pre';
  const { data, isLoading } = useQuery({
    queryKey: ['winprob', matchId],
    queryFn: () => getWinProb(matchId),
    refetchInterval: 60_000,
    enabled: enableSnapshots,
  });
  const snaps = data?.snapshots ?? [];

  // Pre-match (or live/done with no snapshots yet): show a stacked bar from bookmaker probs.
  if (snaps.length < 2) {
    if (!preMatch) return null;
    if (enableSnapshots && isLoading) return <section className="card p-4 text-xs text-dim">Loading momentum…</section>;
    return <PreMatchBar p={preMatch} homeName={homeName} awayName={awayName} />;
  }

  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const xFor = (m: number) => PAD_L + (Math.min(120, Math.max(0, m)) / 120) * innerW;
  const yFor = (p: number) => PAD_T + innerH - p * innerH;

  const homePath = snaps.map((s, i) => `${i === 0 ? 'M' : 'L'} ${xFor(s.minute).toFixed(1)} ${yFor(s.p_home).toFixed(1)}`).join(' ');
  const drawPath = snaps.map((s, i) => `${i === 0 ? 'M' : 'L'} ${xFor(s.minute).toFixed(1)} ${yFor(s.p_draw).toFixed(1)}`).join(' ');
  const awayPath = snaps.map((s, i) => `${i === 0 ? 'M' : 'L'} ${xFor(s.minute).toFixed(1)} ${yFor(s.p_away).toFixed(1)}`).join(' ');

  const last = snaps[snaps.length - 1];
  const labels = [
    { name: homeName, p: last.p_home, cls: 'text-pitch-400', stroke: COLORS.home },
    { name: 'Draw', p: last.p_draw, cls: 'text-dim', stroke: COLORS.draw },
    { name: awayName, p: last.p_away, cls: 'text-cyan-400', stroke: COLORS.away },
  ];

  return (
    <section className="card p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-xs uppercase tracking-widest text-dim">Live win probability</h2>
        <span className="text-[10px] text-dim">snapshot every ~2 min</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block">
        <line x1={PAD_L} y1={PAD_T + innerH / 2} x2={W - PAD_R} y2={PAD_T + innerH / 2} stroke="#262626" strokeWidth="0.5" strokeDasharray="2 2" />
        {[15, 30, 45, 60, 75, 90].map((m) => (
          <g key={m}>
            <line x1={xFor(m)} y1={PAD_T} x2={xFor(m)} y2={PAD_T + innerH} stroke="#1f1f1f" strokeWidth="0.5" />
            <text x={xFor(m)} y={H - 4} fontSize="8" fill="#737373" textAnchor="middle">{m}'</text>
          </g>
        ))}
        <path d={drawPath} stroke="#737373" strokeWidth="1.2" fill="none" />
        <path d={awayPath} stroke="#06b6d4" strokeWidth="1.5" fill="none" />
        <path d={homePath} stroke="#22c55e" strokeWidth="1.5" fill="none" />
      </svg>
      <div className="flex justify-between text-[10px] mt-1">
        {labels.map((l) => (
          <div key={l.name} className={l.cls}>
            <span className="inline-block w-2 h-2 mr-1 align-middle rounded-full" style={{ background: l.stroke }} />
            <span className="truncate">{l.name}</span> <span className="font-mono">{(l.p * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function PreMatchBar({ p, homeName, awayName }: {
  p: { home: number; draw: number; away: number };
  homeName: string;
  awayName: string;
}) {
  const sum = p.home + p.draw + p.away || 1;
  const ph = (p.home / sum) * 100;
  const pd = (p.draw / sum) * 100;
  const pa = (p.away / sum) * 100;
  return (
    <section className="card p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-xs uppercase tracking-widest text-dim">Pre-match win probability</h2>
        <span className="text-[10px] text-dim">bookmaker-implied · live chart at KO</span>
      </div>
      <div className="h-3 rounded-full overflow-hidden flex bg-ink-800">
        <div title={`${homeName} ${ph.toFixed(0)}%`} style={{ width: `${ph}%`, background: COLORS.home }} />
        <div title={`Draw ${pd.toFixed(0)}%`} style={{ width: `${pd}%`, background: COLORS.draw }} />
        <div title={`${awayName} ${pa.toFixed(0)}%`} style={{ width: `${pa}%`, background: COLORS.away }} />
      </div>
      <div className="flex justify-between text-[10px] mt-1.5">
        <div className="text-pitch-400">
          <span className="inline-block w-2 h-2 mr-1 align-middle rounded-full" style={{ background: COLORS.home }} />
          <span className="truncate">{homeName}</span> <span className="font-mono">{ph.toFixed(0)}%</span>
        </div>
        <div className="text-dim">
          <span className="inline-block w-2 h-2 mr-1 align-middle rounded-full" style={{ background: COLORS.draw }} />
          Draw <span className="font-mono">{pd.toFixed(0)}%</span>
        </div>
        <div className="text-cyan-400">
          <span className="inline-block w-2 h-2 mr-1 align-middle rounded-full" style={{ background: COLORS.away }} />
          <span className="truncate">{awayName}</span> <span className="font-mono">{pa.toFixed(0)}%</span>
        </div>
      </div>
    </section>
  );
}
